import PropTypes from 'prop-types';
// @mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton } from '@mui/material';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// components
import Iconify from '../../../components/iconify';
//
import AccountPopover from './AccountPopover';
import NotificationsPopover from './NotificationsPopover';
import Searchbar from './Searchbar';

// ----------------------------------------------------------------------

const NAV_WIDTH = 280;

const HEADER_MOBILE = 64;

const HEADER_DESKTOP = 92;

const StyledRoot = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isDesktopNavOpen',
})(({ theme, isDesktopNavOpen }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  boxShadow: 'none',
  // iOS-style liquid glass on phones; keep the calmer blur on desktop.
  [theme.breakpoints.down('lg')]: {
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
    backgroundImage: `linear-gradient(
      180deg,
      ${alpha('#FFFFFF', 0.58)} 0%,
      ${alpha('#FFFFFF', 0.14)} 55%,
      ${alpha('#FFFFFF', 0.04)} 100%
    )`,
    backdropFilter: 'saturate(190%) blur(28px)',
    WebkitBackdropFilter: 'saturate(190%) blur(28px)',
    borderBottom: `1px solid ${alpha('#FFFFFF', 0.45)}`,
    boxShadow: `0 8px 28px ${alpha(theme.palette.grey[900], 0.08)}, inset 0 -1px 0 ${alpha('#FFFFFF', 0.35)}`,
  },
  [theme.breakpoints.up('lg')]: {
    width: isDesktopNavOpen ? `calc(100% - ${NAV_WIDTH + 1}px)` : '100%',
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: HEADER_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: HEADER_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

// ----------------------------------------------------------------------

Header.propTypes = {
  onOpenNav: PropTypes.func,
  onToggleDesktopNav: PropTypes.func,
  isDesktopNavOpen: PropTypes.bool,
};

export default function Header({ onOpenNav, onToggleDesktopNav, isDesktopNavOpen }) {
  return (
    <StyledRoot isDesktopNavOpen={isDesktopNavOpen}>
      <StyledToolbar>
        <IconButton
          onClick={onOpenNav}
          sx={{
            mr: 1,
            color: 'text.primary',
            display: { lg: 'none' },
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.4),
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: (theme) => `1px solid ${alpha('#fff', 0.45)}`,
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.55),
            },
          }}
        >
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>

        <IconButton
          onClick={onToggleDesktopNav}
          sx={{
            mr: 1,
            color: 'text.primary',
            display: { xs: 'none', lg: 'inline-flex' },
          }}
        >
          <Iconify icon={isDesktopNavOpen ? 'eva:arrow-ios-back-fill' : 'eva:arrow-ios-forward-fill'} />
        </IconButton>

        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-end', sm: 'center' },
            px: { xs: 0.5, sm: 2 },
            minWidth: 0,
          }}
        >
          <Searchbar />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={{
            xs: 0.5,
            sm: 1,
          }}
        >
          <NotificationsPopover />
          <AccountPopover />
        </Stack>
      </StyledToolbar>
    </StyledRoot>
  );
}
